import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Map } from "immutable";

import i18n from "core/i18n";
import useFormRequest from "core/hookForm/hooks/useFormRequest";
import { useHasRole } from "core/hooks";
import { sentry } from "core/util";
import { Roles } from "enums";
import vehicleChange from "serverApi/vehicleChange";

import { getPolicyHolder } from "../../../selectors";

import { SignatureType } from "../types/VehicleSubjectChangeFormValues";

export default function useStepDocuments(nextStep: () => void) {
    const { t } = i18n.useTranslation();
    const form = useFormContext();
    const idChange = form.getValues().idChange;
    const signatureType = form.getValues().signature.type;
    const handleRequest = useFormRequest(form);
    const uploadedFiles = form.watch("signature.uploadedFiles");
    const uploadedFile: Map<any, any> = uploadedFiles[0] ?? null;
    const setValue = form.setValue;

    const [searchParams, setSearchParams] = useSearchParams();

    const [documents, setDocuments] = useState([]);
    const [isDocumentsFetching, setIsDocumentsFetching] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const href = (idChange: string, idDocument: string) => vehicleChange.getDocumentDownloadUrl(idChange, idDocument);

    const isAuthenticated = useHasRole(Roles.AUTHENTICATED);
    const policyHolder = useSelector(getPolicyHolder);

    const emailInfoText = isAuthenticated
        ? t("vehicle.change.vehicleData.documents.emailInfoAuthenticated", { email: policyHolder?.email })
        : t("vehicle.change.vehicleData.documents.emailInfoOneTime");

    useEffect(() => {
        const getDocuments = async () => {
            const [error, res] = await vehicleChange.getDocuments(idChange);

            if (error) {
                setIsDocumentsFetching(false);
                return;
            }

            if (res.ok && res.body) {
                setDocuments(res.body);
            }

            setIsDocumentsFetching(false);
        };

        getDocuments();
    }, []);

    function getIsSubmitButtonDisabled() {
        switch (signatureType) {
            case SignatureType.PHYSICAL:
                return uploadedFile ? false : true;
            default:
                return true;
        }
    }

    async function uploadDocument(): Promise<void> {
        if (!uploadedFile) return;

        return new Promise<void>((resolve, reject) => {
            const onError = () => {
                reject(new Error("Upload failed"));
            };

            const onSuccess = () => {
                resolve();
            };

            handleRequest(() => vehicleChange.uploadSignedRequestDocument(idChange, uploadedFile), onError, onSuccess);
        });
    }

    async function completeRequest(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const onError = () => {
                setIsSubmitting(false);
                reject(new Error("Complete request failed"));
            };

            const onSuccess = () => {
                setIsSubmitting(false);
                searchParams.set("step", "4");
                setSearchParams(searchParams);
                nextStep();
                resolve();
            };

            handleRequest(() => vehicleChange.completeChange(idChange), onError, onSuccess);
        });
    }

    async function submit() {
        setIsSubmitting(true);

        try {
            setIsSubmitting(true);

            switch (signatureType) {
                case SignatureType.PHYSICAL:
                    await uploadDocument();
                    await completeRequest();
                    break;

                case SignatureType.ELECTRONIC:
                    await completeRequest();
                    break;

                default:
                    setIsSubmitting(false);
                    break;
            }
        } catch (e) {
            sentry.error("Submit failed:", e);
            setIsSubmitting(false);
        }
    }

    return {
        idChange,
        setValue,
        documents,
        href,
        isDocumentsFetching,
        signatureType,
        uploadedFile,
        submit,
        isSubmitting,
        getIsSubmitButtonDisabled,
        emailInfoText,
    };
}
